import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { logout, getToken, getSessionId, getTabId } from '../../utils/auth';
import { toast } from 'react-toastify'; 

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isPainelConnected, setIsPainelConnected] = useState(false);
  const [isSessionConnected, setIsSessionConnected] = useState(false);
  
  const painelSocketRef = useRef(null);
  const sessionSocketRef = useRef(null);

  useEffect(() => {
    if (painelSocketRef.current) return;

    const rawBase = window.env?.REACT_APP_SOCKET || process.env.REACT_APP_SOCKET || window.location.origin;
    const base = rawBase.replace(/\/$/, '');
    const path = process.env.REACT_APP_SOCKET_PATH || '/socket.io/';
    const ns = process.env.REACT_APP_SOCKET_NS || '/ws';

    const commonOpts = {
      path,
      // 🔥 Forçando APENAS websocket corta os overheads de rede
      transports: ['websocket'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: !!getToken(),
      auth: { token: getToken(), tabId: getTabId() },
    };

    const painel = io(base, commonOpts);
    painelSocketRef.current = painel;

    const session = io(`${base}${ns}`, commonOpts);
    sessionSocketRef.current = session;

    painel.on('connect', () => setIsPainelConnected(true));
    painel.on('disconnect', () => setIsPainelConnected(false));
    painel.on('connect_error', () => setIsPainelConnected(false));

    session.on('connect', () => {
      setIsSessionConnected(true);
      session.emit('register', { token: getToken(), tabId: getTabId() });
    });
    session.on('disconnect', () => setIsSessionConnected(false));
    session.on('connect_error', () => setIsSessionConnected(false));
    
    session.on('online_users', (users) => setOnlineUsers(users || []));
    
    session.on('session_force_logout', ({ newSessionId, tabId: kickerTabId }) => {
      if (kickerTabId === getTabId()) return;
      const mySessionId = getSessionId();

      if (!mySessionId || (newSessionId && mySessionId !== newSessionId)) {
        painel.disconnect();
        session.disconnect();
        logout();
        toast.warning("Sessão encerrada: Login detectado em outro local.", { autoClose: false });
        window.location.href = '/login';
      }
    });

    const handleTokenUpdate = (e) => {
      // 🔥 IGNORA se a mudança no storage não for no TOKEN
      if (e && e.key && e.key !== 'token') return;

      const t = getToken();
      const tabId = getTabId();
      
      // Atualizar a auth sem derrubar a rede imediatamente
      if (painelSocketRef.current) painelSocketRef.current.auth = { token: t, tabId };
      if (sessionSocketRef.current) sessionSocketRef.current.auth = { token: t, tabId };

      if (t) {
        if (painelSocketRef.current?.disconnected) painelSocketRef.current.connect();
        if (sessionSocketRef.current?.disconnected) sessionSocketRef.current.connect();
      } else {
        if (painelSocketRef.current) painelSocketRef.current.disconnect();
        if (sessionSocketRef.current) sessionSocketRef.current.disconnect();
        setOnlineUsers([]);
        setIsPainelConnected(false);
        setIsSessionConnected(false);
      }
    };

    window.addEventListener('storage', handleTokenUpdate);
    window.addEventListener('token-updated', handleTokenUpdate);

    return () => {
      window.removeEventListener('storage', handleTokenUpdate);
      window.removeEventListener('token-updated', handleTokenUpdate);
      if (painelSocketRef.current) painelSocketRef.current.close();
      if (sessionSocketRef.current) sessionSocketRef.current.close();
      painelSocketRef.current = null;
      sessionSocketRef.current = null;
    };
  }, []);

  return (
    <SocketContext.Provider value={{ 
      painelSocket: painelSocketRef.current, 
      sessionSocket: sessionSocketRef.current, 
      onlineUsers,
      isPainelConnected,
      isSessionConnected
    }}>
      {children}
    </SocketContext.Provider>
  );
};

export const usePainelSocket = () => useContext(SocketContext)?.painelSocket;
export const useSessionSocket = () => useContext(SocketContext)?.sessionSocket;
export const useOnlineUsers = () => useContext(SocketContext)?.onlineUsers;
export const useSocket = usePainelSocket;

export const useSocketStatus = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) return { connected: false, painelConnected: false };
  return { 
    connected: ctx.isSessionConnected,
    painelConnected: ctx.isPainelConnected 
  };
};